// REFACTOR TARGET: keep behavior identical
import Phaser from 'phaser';
import {
  BOARD_VIEWPORT_ZOOM_FROM_DIFFICULTY,
  BOARD_CONTENT_FILL_RATIO,
  BOARD_SAFE_FIT_RATIO,
  SCENE_KEYS,
  SNAP_RADIUS,
  TRAY_MAX_SOURCE_UPSCALE_PHYSICAL,
  TRAY_MIN_PIECE_CSS_PX,
  TRAY_SIZE_REFERENCE_BY_DIFFICULTY,
  TRAY_TUNED_DIFFICULTIES,
} from '../config/constants';
import {
  getPuzzleView,
  getTutorialStatus,
  clearActivePuzzle,
  completePuzzle,
  getNextPuzzleInPack,
  setTutorialStatus,
  startPackPuzzle,
} from '../services/GameState';
import { load as loadProfile } from '../services/ProfileStorage';
import { getPuzzleMeta } from '../services/ContentRepository';
import { UiOverlay } from '../services/UiOverlay';
import { PuzzleLayout } from '../puzzle/layout/PuzzleLayout';
import { PieceTextureFactory } from '../puzzle/pieces/PieceTextureFactory';
import { generateEdgesGrid } from '../puzzle/pieces/JigsawEdgeGrid';
import { TrayPanelRex } from '../puzzle/tray/TrayPanelRex';
import { CustomDragController } from '../puzzle/input/CustomDragController';
import { BoardController } from '../puzzle/board/BoardController';
import { BoardViewportController } from '../puzzle/viewport/BoardViewportController';
import { PuzzleProgress } from '../puzzle/progress/PuzzleProgress';
import type { EdgesGrid } from '../puzzle/pieces/JigsawEdgeGrid';
import {
  getEdgePathPointsNorm,
  normToWorld,
  type EdgeOrientation,
} from '../puzzle/effects/SeamGlowPath';
import { resolvePublicAssetPath } from '../utils/paths';
import { on, off } from '../services/EventBus';
import { soundManager } from '../services/SoundManager';
import {
  loadPuzzleState,
  loadPuzzleTrayOrder,
  savePuzzleState,
  savePuzzleTrayOrder,
  type PuzzleStateMap,
} from '../services/PuzzleStateStorage';
import {
  buildGridPieces,
  getSeedForPuzzle,
  getRowsCols,
  type PieceState,
} from '../services/puzzleCore';
import { TutorialController } from '../puzzle/tutorial/TutorialController';
import type { TutorialMode } from '../puzzle/tutorial/types';

/**
 * Puzzle: превью картинки в Phaser между topbar и tray, DOM HUD поверх.
 * Board (центр экрана) + tray (нижняя зона), детали с crop и перетаскиванием.
 */

function tabRatioFor(totalPieces: number): number {
  if (totalPieces >= 252) return 0.16;
  if (totalPieces >= 114) return 0.18;
  if (totalPieces >= 84) return 0.2;
  return 0.22;
}

export class PuzzleScene extends Phaser.Scene {
  private trayPanel: any | null = null;
  private trayPanelRex: TrayPanelRex | null = null;
  private trayBackgroundGraphics: Phaser.GameObjects.Graphics | null = null;
  private customDragController: CustomDragController | null = null;
  private boardController: BoardController | null = null;
  private boardViewportController: BoardViewportController | null = null;
  private puzzleProgress: PuzzleProgress | null = null;
  /** Мягкое затемнение фона вне поля во время drag (контейнер с 4 rect, alpha твинится). */
  private focusOverlayContainer: Phaser.GameObjects.Container | null = null;
  private pieceTextureKeys: string[] = [];
  /** Контент-сайзер лотка (для returnPieceToTray). */
  private trayContentSizer: any = null;
  /** true во время buildPuzzlePieces — не сохраняем, чтобы не перезаписать неполным состоянием. */
  private isBuildingPuzzle = false;
  private saveStateTimeout: ReturnType<typeof setTimeout> | null = null;
  /** Текущая сетка краёв и tabRatio для эффекта свечения швов (из buildPuzzlePieces). */
  private currentEdgesGrid: EdgesGrid | null = null;
  private currentTabRatio = 0;
  private readonly TRAY_SLIDER_H = 12;
  private readonly TRAY_GAP = 8;
  private readonly TRAY_REFERENCE_DIFFICULTY = 24;
  private trayPieceDisplayW = 0;
  private trayPieceDisplayH = 0;
  private mode: TutorialMode = 'normal';
  private returnTarget: 'hub' | 'collection' | 'pack' = 'hub';
  private returnPackId: string | null = null;
  private tutorialController: TutorialController | null = null;
  private tutorialLayoutTick = 0;
  /** true, пока показываем completion-flow; нужен, чтобы не мигал debug-экран "Текущий пазл". */
  private isCompletingPuzzle = false;
  /** Высота .puzzle-tray-scroll в px после buildTrayPanel; восстанавливаем в DOM после refreshPuzzle (tutorial step 2). */
  private trayScrollHeightPx = 0;
  private resizeHandler = (): void => {
    const cam = this.cameras?.main;
    if (!cam || !this.scene.isActive()) return;
    cam.setViewport(0, 0, this.scale.width, this.scale.height);

    // DOM → viewport (при наличии DOM), иначе fallback-геометрия лотка.
    const fromDom = this.getTrayRectViewportFromDom();
    const tr = fromDom ?? this.getTrayRect();

    if (this.trayPanel) {
      const panel = this.trayPanel as any;
      this.trayPanel.setPosition(tr.x + tr.w / 2, tr.y + tr.h / 2);
      if (typeof panel.setMinSize === 'function') {
        panel.setMinSize(tr.w, tr.h);
      }
      if (typeof panel.resize === 'function') {
        panel.resize(tr.w, tr.h);
      }
      if (typeof panel.setSize === 'function') {
        panel.setSize(tr.w, tr.h);
      }
      this.drawTrayBackground();
      if (typeof panel.layout === 'function') {
        panel.layout();
      }
      this.ensureTrayScrollStateForTutorial();
    }

    this.customDragController?.updateRect(tr, this.TRAY_SLIDER_H);
    this.tutorialController?.layout();
    this.boardViewportController?.handleResize();
  };

  constructor() {
    super({ key: SCENE_KEYS.Puzzle });
  }

  private setPuzzleLoadingOverlayVisible(visible: boolean): void {
    if (visible) {
      UiOverlay.showPuzzleLoadingOverlay();
    } else {
      UiOverlay.hidePuzzleLoadingOverlay();
    }
  }

  private shouldShowPuzzleLoadingOverlay(pieceCount: number): boolean {
    return this.mode !== 'tutorial' && pieceCount >= 84;
  }

  /** При запуске с initialStep === 2 туториал продолжается с шага 2 (шаг 1 уже сделан). */
  private tutorialInitialStep: 1 | 2 = 1;

  init(data?: {
    mode?: string;
    initialStep?: 1 | 2;
    fromCollection?: boolean;
    returnTarget?: 'hub' | 'collection' | 'pack';
    returnPackId?: string;
  }): void {
    this.isCompletingPuzzle = false;
    const status = getTutorialStatus();
    const wantTutorial =
      data?.mode === 'tutorial' &&
      (status === 'none' || status === 'step2' || data?.fromCollection === true);
    this.mode = wantTutorial ? 'tutorial' : 'normal';
    this.tutorialInitialStep = data?.initialStep ?? 1;
    this.returnTarget = data?.returnTarget ?? 'hub';
    this.returnPackId = data?.returnPackId ?? null;
  }

  /** Rect лотка в viewport (fallback без DOM). */
  private getTrayRect(): { x: number; y: number; w: number; h: number } {
    return PuzzleLayout.computeTrayRect(this);
  }

  private isDesktopLayout(): boolean {
    return typeof window !== 'undefined' && window.matchMedia('(min-width: 900px)').matches;
  }

  /**
   * Rect DOM-лотка в координатах viewport камеры.
   * Берём именно внутреннюю область скролла (.puzzle-tray-scroll),
   * чтобы кусочки попадали ровно в пунктирный прямоугольник.
   */
  getTrayRectViewportFromDom(): { x: number; y: number; w: number; h: number } | null {
    const domRect = UiOverlay.getTrayScrollDomRect?.() ?? null;
    if (!domRect) return null;
    const canvasRect = this.game.canvas.getBoundingClientRect();
    const cam = this.cameras.main;
    if (canvasRect.width <= 0 || canvasRect.height <= 0) return null;
    const scaleX = cam.width / canvasRect.width;
    const scaleY = cam.height / canvasRect.height;
    const viewportX = (domX: number) => (domX - canvasRect.left) * scaleX;
    const viewportY = (domY: number) => (domY - canvasRect.top) * scaleY;
    const tr = {
      x: viewportX(domRect.left),
      y: viewportY(domRect.top),
      w: domRect.width * scaleX,
      h: domRect.height * scaleY,
    };
    const canvasBottom = canvasRect.top + canvasRect.height;
    const trayInView =
      tr.y >= 0 && tr.y + tr.h <= cam.height;
    const canvasCoversTray = canvasBottom >= domRect.top;
    if (!trayInView && !canvasCoversTray) {
      const clampedY = Math.max(0, cam.height - tr.h);
      return { ...tr, y: clampedY };
    }
    return tr;
  }

  /** Rect полного DOM-лотка (#puzzle-tray) в координатах viewport для отрисовки фона на канвасе. */
  private getFullTrayRectViewportFromDom(): { x: number; y: number; w: number; h: number } | null {
    const domRect = UiOverlay.getTrayDomRect?.() ?? null;
    if (!domRect) return null;
    const canvasRect = this.game.canvas.getBoundingClientRect();
    const cam = this.cameras.main;
    if (canvasRect.width <= 0 || canvasRect.height <= 0) return null;
    const scaleX = cam.width / canvasRect.width;
    const scaleY = cam.height / canvasRect.height;
    return {
      x: (domRect.left - canvasRect.left) * scaleX,
      y: (domRect.top - canvasRect.top) * scaleY,
      w: domRect.width * scaleX,
      h: domRect.height * scaleY,
    };
  }

  private drawTrayBackground(): void {
    const fullTr = this.getFullTrayRectViewportFromDom();
    if (!fullTr) return;
    if (this.trayBackgroundGraphics) this.trayBackgroundGraphics.destroy();
    this.trayBackgroundGraphics = this.add
      .graphics()
      .setScrollFactor(0)
      .setDepth(500);
    this.trayBackgroundGraphics.fillStyle(0x2c2c2c, 1);
    this.trayBackgroundGraphics.fillRoundedRect(fullTr.x, fullTr.y, fullTr.w, fullTr.h, 0);
    this.trayBackgroundGraphics.fillStyle(0xffffff, 0.05);
    this.trayBackgroundGraphics.fillRect(fullTr.x, fullTr.y, fullTr.w, 2);
  }

  /**
   * Возвращает кусок в лоток: показывает оригинал, добавляет в sizer при необходимости, layout, чистит custom drag.
   * pieceId — для лога и поиска; originalImg — исходный Image из лотка (если null, ищется в trayContentSizer по pieceId).
   */
  private returnPieceToTray(
    pieceId: number,
    originalImg: Phaser.GameObjects.Image | null
  ): void {
    let img = originalImg;
    if (!img && this.trayContentSizer) {
      const children = (this.trayContentSizer as any).getChildren?.() ?? [];
      for (const child of children) {
        if (child.getData?.('pieceId') === pieceId) {
          img = child as Phaser.GameObjects.Image;
          break;
        }
      }
    }
    if (img) {
      const tileW = img.getData('tileW') as number | undefined;
      const tileH = img.getData('tileH') as number | undefined;
      if (typeof tileW === 'number' && typeof tileH === 'number') {
        img.setDisplaySize(tileW, tileH);
      }
      // Лоток: tileW/tileH = board piece size; rexUI Sizer масштабирует детей (scale < 1). Не ставим setScale(1).
      const ref = this.getTrayImagesForDrag().find((i) => i.getData?.('pieceId') !== pieceId);
      if (ref) {
        img.setScale(ref.scaleX, ref.scaleY);
      }
      img.setVisible(true);
      img.setAlpha(1);
      const content = this.trayContentSizer;
      if (content) {
        const inSizer = (content as any).getChildren?.()?.includes?.(img) ?? false;
        if (!inSizer && img.scene) {
          (content as any).add(img, 0, 'center', 0, false);
        }
        this.updateTrayContentMinSize();
      }
    }
    this.saveState();
  }

  /** Текущий список картинок в лотке (включая возвращённые с доски) — для CustomDragController. */
  private getTrayImagesForDrag(): Phaser.GameObjects.Image[] {
    const content = this.trayContentSizer as any;
    const children = content?.getChildren?.() ?? [];
    return children.filter(
      (c: Phaser.GameObjects.GameObject) => c && (c.getData?.('pieceId') != null)
    ) as Phaser.GameObjects.Image[];
  }

  /** Скрыть/показать скроллбар лотка по горизонтальному переполнению (rexUI для scrollMode 1 проверяет вертикаль). */
  private updateTraySliderVisibility(): void {
    const panel = this.trayPanel as any;
    const content = this.trayContentSizer as any;
    if (panel?.childrenMap?.slider == null || !content) return;
    const contentW = (content.width ?? 0) as number;
    const panelW = panel.width ?? 0;
    const slider = panel.childrenMap.slider as Phaser.GameObjects.GameObject & { setVisible: (v: boolean) => void };
    if (typeof slider?.setVisible === 'function') slider.setVisible(contentW > panelW);
  }

  /** Пересчитать minSize контента лотка по числу кусков (убирает лишний скролл при 6 и т.д.). */
  private updateTrayContentMinSize(): void {
    const content = this.trayContentSizer as any;
    if (!content || typeof content.getChildren !== 'function' || typeof content.setMinSize !== 'function') return;
    const children = content.getChildren() as Phaser.GameObjects.GameObject[];
    const n = children.length;
    const { w: tileW, h: tileH } = this.getTrayPieceSize();
    const w = n <= 0 ? 0 : n * tileW + (n - 1) * this.TRAY_GAP;
    const h = tileH;
    content.setMinSize(w, h);
    content.layout?.();
    this.trayPanel?.layout?.();
    this.updateTraySliderVisibility();
    this.ensureTrayScrollStateForTutorial();
  }

  /**
   * Индекс в лотке для вставки возвращаемого куска — середина видимой области (по текущему скроллу).
   */
  private getTrayVisibleInsertIndex(): number {
    const panel = this.trayPanel as any;
    const content = this.trayContentSizer as any;
    if (!panel || !content) return 0;
    const block = panel.childrenMap?.scrollableBlock ?? panel.childrenMap?.child ?? panel;
    const scrollMode = block?.scrollMode ?? panel?.scrollMode ?? 0;
    const scrollVal = scrollMode === 1 ? (panel.childOY as number) : (panel.childOX as number);
    if (typeof scrollVal !== 'number') return 0;
    const { w: tileW } = this.getTrayPieceSize();
    const itemWidth = tileW + this.TRAY_GAP;
    if (itemWidth <= 0) return 0;
    const children = content.getChildren?.() ?? [];
    const pixelsScrolled = scrollMode === 1 ? -scrollVal : -scrollVal;
    const firstVisible = Math.floor(pixelsScrolled / itemWidth);
    const panelW = panel.width ?? 0;
    const visibleCount = Math.max(1, Math.floor(panelW / itemWidth));
    const midIndex = firstVisible + Math.floor(visibleCount / 2);
    return Phaser.Math.Clamp(midIndex, 0, children.length);
  }

  /**
   * Возврат куска с доски в лоток: вставляет в середину видимой области и плавно проявляет (fade + лёгкий scale).
   * Размер всегда берём из tray-size; после layout принудительно восстанавливаем — Rex Sizer может перезаписать display size.
   */
  private returnBoardPieceToTray(
    pieceId: number,
    pieceKey: string,
    _tileW: number,
    _tileH: number
  ): void {
    this.boardController?.unplacePiece(pieceId);
    this.updatePuzzleProgressDOM();
    const content = this.trayContentSizer as any;
    if (!content) return;
    const { w: tileW, h: tileH } = this.getTrayPieceSize({ w: _tileW, h: _tileH });
    const img = this.add.image(0, 0, pieceKey).setOrigin(0.5, 0.5).setDisplaySize(tileW, tileH);
    img.setData('pieceId', pieceId);
    img.setData('pieceKey', pieceKey);
    img.setData('tileW', tileW);
    img.setData('tileH', tileH);
    img.setDepth(2000);
    img.setAlpha(0);
    img.setInteractive(
      new Phaser.Geom.Rectangle(-tileW / 2, -tileH / 2, tileW, tileH),
      Phaser.Geom.Rectangle.Contains,
      true
    );
    this.input.setDraggable(img);
    const insertIndex = this.getTrayVisibleInsertIndex();
    if (typeof content.insert === 'function') {
      content.insert(insertIndex, img, 0, 'center', 0, false);
    } else {
      content.add(img, 0, 'center', 0, false);
    }
    this.updateTrayContentMinSize();
    content.layout?.();
    this.trayPanel?.layout?.();
    this.ensureTrayScrollStateForTutorial();
    // Sizer перезаписывает display size — задаём размер и анимацию через displayWidth/displayHeight (не scale).
    const startW = tileW * 0.92;
    const startH = tileH * 0.92;
    img.setDisplaySize(startW, startH);
    this.tweens.add({
      targets: img,
      displayWidth: tileW,
      displayHeight: tileH,
      alpha: 1,
      duration: 260,
      ease: 'Quad.easeOut',
      onComplete: () => {
        img.setDisplaySize(tileW, tileH);
      },
    });
    this.saveState();
  }

  /** Вызывается PuzzleProgress один раз при placed === total (подписка onCompleted). */
  private showPuzzleCompleteFlow(): void {
    if (this.isCompletingPuzzle) return;
    const view = getPuzzleView();
    const active = view?.activePuzzle;
    if (!active) return;
    this.isCompletingPuzzle = true;
    const puzzleId = active.puzzleId;
    const difficulty = active.difficulty;
    if (this.mode === 'tutorial') {
      setTutorialStatus('completed');
      this.tutorialController?.hideWelcomeHeader();
    }
    const completionSource =
      this.mode === 'tutorial'
        ? ('tutorial' as const)
        : (loadProfile().activePuzzle?.source as 'daily' | 'pack' | 'collection' | undefined);
    const uiSource = this.mode === 'tutorial' ? 'tutorial' : (loadProfile().activePuzzle?.source ?? 'daily');
    soundManager.playVictory();
    completePuzzle(puzzleId as import('../data/types').PuzzleId, difficulty, completionSource);
    this.playCompletionCelebration();
    const boardRectPx = (() => {
      const br = this.boardController?.getRectBox() ?? null;
      if (!br) return null;
      const cam = this.cameras.main;
      const canvasRect = this.game.canvas.getBoundingClientRect();
      if (canvasRect.width <= 0 || canvasRect.height <= 0) return null;
      const pxPerViewportX = canvasRect.width / cam.width;
      const pxPerViewportY = canvasRect.height / cam.height;
      return {
        left: canvasRect.left + br.x * pxPerViewportX,
        top: canvasRect.top + br.y * pxPerViewportY,
        width: br.w * pxPerViewportX,
        height: br.h * pxPerViewportY,
      };
    })();
    const nextInPack = getNextPuzzleInPack(puzzleId as import('../data/types').PuzzleId);
    this.time.delayedCall(420, () => {
      UiOverlay.showPuzzleComplete(
        {
          source: uiSource as 'daily' | 'pack' | 'collection' | 'tutorial',
          hasNextInPack: uiSource === 'pack' && nextInPack != null,
          boardRectPx,
        },
        {
          onGoToCollection: () => {
            import('./Collection').then((mod) => {
              if (!this.scene.get(SCENE_KEYS.Collection)) {
                this.scene.add(SCENE_KEYS.Collection, mod.Collection, false);
              }
              this.scene.start(SCENE_KEYS.Collection);
            });
          },
          onGoToHub: () => {
            import('./Hub').then((mod) => {
              if (!this.scene.get(SCENE_KEYS.Hub)) {
                this.scene.add(SCENE_KEYS.Hub, mod.Hub, false);
              }
              this.scene.start(SCENE_KEYS.Hub);
            });
          },
          onNextPuzzle:
            nextInPack != null
              ? () => {
                  startPackPuzzle(nextInPack.puzzleId, difficulty);
                  this.scene.start(SCENE_KEYS.Puzzle);
                }
              : undefined,
        }
      );
    });
  }

  /** Переход на Hub с динамической регистрацией сцены (нужно, если зашли в пазл через туториал и Hub ещё не добавлен). */
  private goToHub(): void {
    import('./Hub').then((mod) => {
      if (!this.scene.get(SCENE_KEYS.Hub)) {
        this.scene.add(SCENE_KEYS.Hub, mod.Hub, false);
      }
      this.scene.start(SCENE_KEYS.Hub);
    });
  }

  private goToCollection(): void {
    import('./Collection').then((mod) => {
      if (!this.scene.get(SCENE_KEYS.Collection)) {
        this.scene.add(SCENE_KEYS.Collection, mod.Collection, false);
      }
      this.scene.start(SCENE_KEYS.Collection);
    });
  }

  private goToPack(packId: string): void {
    import('./Pack').then((mod) => {
      if (!this.scene.get(SCENE_KEYS.Pack)) {
        this.scene.add(SCENE_KEYS.Pack, mod.Pack, false);
      }
      this.scene.start(SCENE_KEYS.Pack, { packId });
    });
  }

  private goToReturnTarget(): void {
    if (this.returnTarget === 'collection') {
      this.goToCollection();
      return;
    }
    if (this.returnTarget === 'pack' && this.returnPackId) {
      this.goToPack(this.returnPackId);
      return;
    }
    this.goToHub();
  }

  /** Салют при полном сборе пазла: серебристые искры/пыль в зоне доски (дольше и мягче). */
  private playCompletionCelebration(): void {
    const r = this.boardController?.getRectBox() ?? null;
    if (!r) return;

    const burstCount = 44;
    const dustCount = 24;
    const silverPalette = [0xffffff, 0xf2f5ff, 0xd6deef, 0xc4cedf];
    const centerX = r.x + r.w / 2;
    const centerY = r.y + r.h / 2;

    for (let i = 0; i < burstCount; i++) {
      const color = silverPalette[i % silverPalette.length];
      const radius = Phaser.Math.FloatBetween(2.2, 5.4);
      const startX = Phaser.Math.Between(Math.floor(r.x - 24), Math.floor(r.x + r.w + 24));
      const startY = Phaser.Math.Between(Math.floor(r.y - 70), Math.floor(r.y + r.h * 0.35));
      const sparkle = this.add
        .circle(startX, startY, radius, color, Phaser.Math.FloatBetween(0.7, 0.95))
        .setDepth(64500)
        .setScrollFactor(0);

      const driftAngle = Phaser.Math.FloatBetween(-Math.PI * 0.85, Math.PI * 0.15);
      const driftDist = Phaser.Math.FloatBetween(90, 210);
      const endX = startX + Math.cos(driftAngle) * driftDist + (startX - centerX) * 0.12;
      const endY = startY + Math.sin(driftAngle) * driftDist + (startY - centerY) * 0.08;

      this.tweens.add({
        targets: sparkle,
        x: endX,
        y: endY,
        alpha: 0,
        scale: Phaser.Math.FloatBetween(1.4, 2.1),
        angle: Phaser.Math.Between(-120, 120),
        duration: Phaser.Math.Between(1900, 2800),
        ease: 'Sine.easeOut',
        onComplete: () => sparkle.destroy(),
      });
    }

    for (let i = 0; i < dustCount; i++) {
      const dust = this.add
        .circle(
          Phaser.Math.Between(Math.floor(r.x), Math.floor(r.x + r.w)),
          Phaser.Math.Between(Math.floor(r.y - 24), Math.floor(r.y + r.h)),
          Phaser.Math.FloatBetween(1.2, 2.6),
          0xffffff,
          Phaser.Math.FloatBetween(0.22, 0.38)
        )
        .setDepth(64490)
        .setScrollFactor(0);
      this.tweens.add({
        targets: dust,
        y: dust.y + Phaser.Math.FloatBetween(22, 58),
        alpha: 0,
        duration: Phaser.Math.Between(1700, 2500),
        ease: 'Sine.easeInOut',
        onComplete: () => dust.destroy(),
      });
    }
  }

  /** Плавающая «+1» над деталью при правильной постановке: поднимается вверх и исчезает. */
  private playFloatingCheckmark(worldX: number, worldY: number): void {
    const offsetY = 28;
    const text = this.add
      .text(worldX, worldY - offsetY, '+1', {
        fontSize: '24px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5, 0.5)
      .setDepth(60000)
      .setScrollFactor(0)
      .setStroke('#2d5016', 3);
    this.tweens.add({
      targets: text,
      y: text.y - 40,
      alpha: 0,
      duration: 1100,
      ease: 'Power2',
      onComplete: () => text.destroy(),
    });
  }

  /** Временное свечение вдоль швов при правильной склейке: бело-голубое, пробегает вдоль края, затем исчезает. */
  private playSeamGlow(args: {
    pieceId: number;
    worldX: number;
    worldY: number;
    sides: ('top' | 'right' | 'bottom' | 'left')[];
    pieceW: number;
    pieceH: number;
  }): void {
    const grid = this.currentEdgesGrid;
    if (!grid || args.sides.length === 0) return;
    const pieceEdges = grid[args.pieceId];
    if (!pieceEdges) return;

    const edgeWorldPoints: { x: number; y: number }[][] = [];
    for (const side of args.sides) {
      const edge = pieceEdges[side];
      const normPoints = getEdgePathPointsNorm(edge, side as EdgeOrientation, this.currentTabRatio);
      const worldPoints = normToWorld(
        normPoints,
        args.worldX,
        args.worldY,
        args.pieceW,
        args.pieceH
      );
      edgeWorldPoints.push(worldPoints);
    }

    const g = this.add.graphics().setScrollFactor(0).setDepth(60001);

    const totalLength = edgeWorldPoints.reduce((sum, pts) => {
      let len = 0;
      for (let i = 1; i < pts.length; i++) {
        len += Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
      }
      return sum + len;
    }, 0);

    const edgeLengths = edgeWorldPoints.map((pts) => {
      let len = 0;
      for (let i = 1; i < pts.length; i++) {
        len += Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
      }
      return len;
    });

    const state = { timeNorm: 0 };
    const lineWidth = 2.5;
    const color = 0xe8f4fc; // бело-голубой с лёгким тёплым оттенком
    const durationMs = 780;
    const runEndNorm = 520 / durationMs;
    const alphaPeakEndNorm = 120 / durationMs;
    const alphaFadeStartNorm = 400 / durationMs;

    const draw = () => {
      const t = state.timeNorm;
      const runProgress = t >= runEndNorm ? 1 : t / runEndNorm;
      let alpha = 0;
      if (t < alphaPeakEndNorm) {
        alpha = (t / alphaPeakEndNorm) * 0.3;
      } else if (t < alphaFadeStartNorm) {
        alpha = 0.3;
      } else if (t < 1) {
        alpha = 0.3 * (1 - (t - alphaFadeStartNorm) / (1 - alphaFadeStartNorm));
      }
      g.clear();
      if (alpha <= 0) return;
      g.lineStyle(lineWidth, color, alpha);
      let drawn = 0;
      const targetDrawn = runProgress * totalLength;
      for (let e = 0; e < edgeWorldPoints.length; e++) {
        const pts = edgeWorldPoints[e];
        const segLen = edgeLengths[e];
        if (drawn >= targetDrawn) break;
        const needFromThis = targetDrawn - drawn;
        const fraction = Math.min(1, needFromThis / segLen);
        g.beginPath();
        g.moveTo(pts[0].x, pts[0].y);
        if (fraction <= 0) {
          drawn += segLen;
          continue;
        }
        const numSegs = pts.length - 1;
        const toIndex = fraction >= 1 ? pts.length : 1 + Math.floor(fraction * numSegs);
        const tSeg = fraction >= 1 ? 1 : (fraction * numSegs) % 1;
        for (let i = 1; i < toIndex; i++) {
          g.lineTo(pts[i].x, pts[i].y);
        }
        if (toIndex <= numSegs && tSeg > 0) {
          const i = toIndex;
          const px = pts[i - 1].x + (pts[i].x - pts[i - 1].x) * tSeg;
          const py = pts[i - 1].y + (pts[i].y - pts[i - 1].y) * tSeg;
          g.lineTo(px, py);
        }
        g.strokePath();
        drawn += segLen;
      }
    };

    this.tweens.add({
      targets: state,
      timeNorm: 1,
      duration: durationMs,
      ease: 'Linear',
      onUpdate: draw,
      onComplete: () => {
        g.destroy();
      },
    });
    this.time.delayedCall(0, draw);

    this.spawnSeamSparkParticles(edgeWorldPoints, totalLength, edgeLengths);
  }

  /**
   * 6–12 маленьких частиц вдоль шва: появляются на линии, медленно расходятся в стороны, исчезают за 400–600 ms.
   */
  private spawnSeamSparkParticles(
    edgeWorldPoints: { x: number; y: number }[][],
    totalLength: number,
    edgeLengths: number[]
  ): void {
    const count = 6 + Math.floor(Math.random() * 7);
    const spreadDist = 10 + Math.random() * 6;

    const getPointAndNormal = (atLength: number): { x: number; y: number; nx: number; ny: number } | null => {
      if (totalLength <= 0) return null;
      let acc = 0;
      for (let e = 0; e < edgeWorldPoints.length; e++) {
        const pts = edgeWorldPoints[e];
        const segLen = edgeLengths[e];
        if (atLength <= acc + segLen) {
          const local = atLength - acc;
          const t = segLen <= 0 ? 0 : Math.min(1, local / segLen);
          const idx = t * (pts.length - 1);
          const i0 = Math.min(Math.floor(idx), pts.length - 2);
          const i1 = i0 + 1;
          const pt = pts[i0];
          const pt1 = pts[i1];
          const segT = idx - i0;
          const x = pt.x + (pt1.x - pt.x) * segT;
          const y = pt.y + (pt1.y - pt.y) * segT;
          const dx = pt1.x - pt.x;
          const dy = pt1.y - pt.y;
          const len = Math.hypot(dx, dy) || 1;
          const nx = -dy / len;
          const ny = dx / len;
          return { x, y, nx, ny };
        }
        acc += segLen;
      }
      const lastEdge = edgeWorldPoints[edgeWorldPoints.length - 1];
      const lastPts = lastEdge;
      const p = lastPts[lastPts.length - 1];
      return p ? { x: p.x, y: p.y, nx: 0, ny: 0 } : null;
    };

    for (let i = 0; i < count; i++) {
      const atLength = totalLength * (0.1 + 0.8 * Math.random());
      const data = getPointAndNormal(atLength);
      if (!data) continue;
      const sign = Math.random() < 0.5 ? -1 : 1;
      const offset = (0.4 + Math.random() * 0.6) * spreadDist * sign;
      const startX = data.x;
      const startY = data.y;
      const endX = data.x + data.nx * offset;
      const endY = data.y + data.ny * offset;

      const size = 1 + Math.random() * 0.8;
      const spark = this.add.graphics().setScrollFactor(0).setDepth(60002);
      const drawSpark = (alpha: number) => {
        spark.clear();
        if (alpha <= 0) return;
        spark.fillStyle(0xfffef8, alpha);
        spark.fillCircle(0, 0, size);
      };
      spark.setPosition(startX, startY);

      const dur = 400 + Math.random() * 200;
      const state = { x: startX, y: startY, alpha: 0.4 };
      drawSpark(state.alpha);

      this.tweens.add({
        targets: state,
        x: endX,
        y: endY,
        alpha: 0,
        duration: dur,
        ease: 'Quad.easeOut',
        onUpdate: () => {
          spark.setPosition(state.x, state.y);
          drawSpark(state.alpha);
        },
        onComplete: () => spark.destroy(),
      });
    }
  }

  /** Обновляет прогресс из доски и DOM .puzzle-progress из progress.getText(), а также полосу прогресса. */
  private updatePuzzleProgressDOM(): void {
    const total = this.boardController?.getTotalPieces() ?? 0;
    const placed = this.boardController?.getPlacedCorrectCount() ?? 0;
    this.puzzleProgress?.update(total, placed);
    const el = document.querySelector('.puzzle-progress');
    if (el) {
      el.textContent = this.puzzleProgress?.getText() ?? '0/0';
    }
    const barFill = document.querySelector<HTMLElement>('.puzzle-progress-bar-fill');
    if (barFill) {
      const pct = total > 0 ? Math.round((placed / total) * 100) : 0;
      barFill.style.width = `${pct}%`;
    }
  }

  /**
   * Сохраняет состояние пазла. immediate=true — сразу (Назад, закрытие вкладки);
   * иначе — с дебаунсом 400 ms, чтобы не перезаписывать подряд.
   */
  private saveState(immediate = false): void {
    if (this.isBuildingPuzzle) return;
    const view = getPuzzleView();
    const active = view?.activePuzzle;
    const totalPieces = this.boardController?.getTotalPieces() ?? 0;
    if (!active || totalPieces <= 0) return;
    if (!this.boardController?.hasPlacedLayer()) return;
    const onBoard = this.boardController.getPlacedPiecesCount();
    const layerCount = this.boardController.getPlacedLayerCount();
    if (onBoard > 0 && layerCount === 0) return;

    // В туториале состояние сохраняем под difficulty = TUTORIAL_PIECE_COUNT (16),
    // т.к. при пересборке (refreshPuzzle → buildPuzzle) загружаем по тому же ключу.
    const storageDifficulty =
      this.mode === 'tutorial' ? PuzzleScene.TUTORIAL_PIECE_COUNT : active.difficulty;
    if (immediate) {
      if (this.saveStateTimeout != null) {
        clearTimeout(this.saveStateTimeout);
        this.saveStateTimeout = null;
      }
      this.flushSaveState(active.puzzleId, storageDifficulty);
      return;
    }
    if (this.saveStateTimeout != null) clearTimeout(this.saveStateTimeout);
    this.saveStateTimeout = setTimeout(() => {
      this.saveStateTimeout = null;
      if (!this.scene.isActive()) return;
      const a = getPuzzleView()?.activePuzzle;
      if (a) {
        const diff =
          this.mode === 'tutorial' ? PuzzleScene.TUTORIAL_PIECE_COUNT : a.difficulty;
        this.flushSaveState(a.puzzleId, diff);
      }
    }, 400);
  }

  private flushSaveState(puzzleId: string, difficulty: number): void {
    if (this.isBuildingPuzzle) return;
    const state = (this.boardController?.collectCurrentState() ?? {}) as unknown as PuzzleStateMap;
    savePuzzleState(puzzleId, difficulty, state);
    const trayOrder = this.getTrayOrderPieceIds();
    if (trayOrder.length > 0) savePuzzleTrayOrder(puzzleId, difficulty, trayOrder);
  }

  private getTrayOrderPieceIds(): number[] {
    const content = this.trayContentSizer as any;
    const children = content?.getChildren?.() ?? [];
    const ids: number[] = [];
    for (const child of children) {
      const pieceId = child?.getData?.('pieceId');
      if (typeof pieceId === 'number') ids.push(pieceId);
    }
    return ids;
  }

  private buildPuzzlePieces(
    textureKey: string,
    imageWidth: number,
    imageHeight: number,
    safeRect: { left: number; top: number; width: number; height: number }
  ): void {
    this.isBuildingPuzzle = true;
    this.destroyBoardAndTray();
    const view = getPuzzleView();
    const active = view?.activePuzzle;
    if (!active) {
      this.isBuildingPuzzle = false;
      return;
    }
    const tr = this.getTrayRectViewportFromDom() ?? this.getTrayRect();
    const difficulty =
      this.mode === 'tutorial' ? PuzzleScene.TUTORIAL_PIECE_COUNT : active.difficulty;
    const puzzleId = active.puzzleId;
    const seed = getSeedForPuzzle(puzzleId, difficulty);
    const { rows, cols } = getRowsCols(difficulty, imageWidth, imageHeight);
    const srcCellW = imageWidth / cols;
    const srcCellH = imageHeight / rows;
    const edgesGrid = generateEdgesGrid(rows, cols, seed);
    const tabRatio = tabRatioFor(difficulty);
    const tabPx = Math.max(0, Math.round(Math.min(srcCellW, srcCellH) * Math.max(0, tabRatio)));
    const sourcePieceW = srcCellW + tabPx * 2;
    const sourcePieceH = srcCellH + tabPx * 2;
    const { pieces } = buildGridPieces(
      imageWidth,
      imageHeight,
      difficulty,
      seed
    );

    this.pieceTextureKeys = PieceTextureFactory.create(
      this,
      textureKey,
      pieces,
      puzzleId,
      difficulty,
      srcCellW,
      srcCellH,
      edgesGrid,
      tabRatio
    );

    if (!this.puzzleProgress) {
      this.puzzleProgress = new PuzzleProgress({
        onCompleted: () => this.showPuzzleCompleteFlow(),
      });
    }
    this.currentEdgesGrid = edgesGrid;
    this.currentTabRatio = tabRatio;
    let trayPieceSize = this.computeTrayPieceSize({
      difficulty,
      imageWidth,
      imageHeight,
      safeRect,
      sourcePieceW,
      sourcePieceH,
    });
    if (this.mode === 'tutorial' && this.isDesktopLayout()) {
      const tutorialDesktopScale = 1.22;
      trayPieceSize = {
        w: trayPieceSize.w * tutorialDesktopScale,
        h: trayPieceSize.h * tutorialDesktopScale,
      };
    }
    if (this.mode !== 'tutorial' && this.isDesktopLayout()) {
      const desktopScale = 0.9;
      trayPieceSize = {
        w: trayPieceSize.w * desktopScale,
        h: trayPieceSize.h * desktopScale,
      };
    }
    // Страховочный лимит: кусок в лотке никогда не должен вылезать по высоте
    // (в т.ч. в туториале после desktop-scale изменений).
    const baseMaxTrayPieceH = Math.max(36, tr.h - this.TRAY_SLIDER_H - this.TRAY_GAP * 1.5);
    const tutorialDesktopBoostPx =
      this.mode === 'tutorial' && this.isDesktopLayout() ? 28 : 0;
    const maxTrayPieceH = Math.min(
      Math.max(36, tr.h - this.TRAY_GAP),
      baseMaxTrayPieceH + tutorialDesktopBoostPx
    );
    if (trayPieceSize.h > maxTrayPieceH) {
      const fitScale = maxTrayPieceH / trayPieceSize.h;
      trayPieceSize = {
        w: trayPieceSize.w * fitScale,
        h: trayPieceSize.h * fitScale,
      };
    }
    this.trayPieceDisplayW = trayPieceSize.w;
    this.trayPieceDisplayH = trayPieceSize.h;
    const boardCallbacks = {
      playFloatingCheckmark: (x: number, y: number) => this.playFloatingCheckmark(x, y),
      playSeamGlow: (args: any) => this.playSeamGlow(args),
      updatePuzzleProgressDOM: () => this.updatePuzzleProgressDOM(),
      saveState: () => this.saveState(),
      returnBoardPieceToTray: (pieceId: number, pieceKey: string, tileW: number, tileH: number) =>
        this.returnBoardPieceToTray(pieceId, pieceKey, tileW, tileH),
      onMagnetJoin: () => soundManager.playSnap(),
    };
    if (!this.boardController) {
      this.boardController = new BoardController(this, {
        snapRadius: SNAP_RADIUS,
        callbacks: boardCallbacks,
      });
    } else {
      this.boardController.setCallbacks(boardCallbacks);
    }
    this.boardController.build({
      safeRect,
      imageWidth,
      imageHeight,
      rows,
      cols,
      pieces,
      tabRatio,
    });
    this.boardViewportController?.destroy();
    this.boardViewportController = null;
    if (difficulty >= BOARD_VIEWPORT_ZOOM_FROM_DIFFICULTY) {
      this.boardViewportController = new BoardViewportController({
        scene: this,
        canvas: this.game.canvas,
        onZoomPercentChange: (percent) => UiOverlay.setPuzzleZoomPercent(percent),
        adapter: {
          getBaseRectBox: () => this.boardController?.getBaseRectBox() ?? null,
          getRectBox: () => this.boardController?.getRectBox() ?? null,
          hasDraggablePieceAtScreenPoint: (x, y) =>
            this.boardController?.hasDraggablePieceAtScreenPoint(x, y) ?? false,
          setViewportTransform: (scale, offsetX, offsetY) =>
            this.boardController?.setViewportTransform(scale, offsetX, offsetY),
        },
      });
      this.boardController.setInteractionBlockedPredicate(() =>
        this.boardViewportController?.isInteractionBlocked() ?? false
      );
      this.boardViewportController.maybeShowOneTimeHint(difficulty);
    } else {
      this.boardController.setInteractionBlockedPredicate(null);
      this.boardController.setViewportTransform(1, 0, 0);
    }
    this.createFocusOverlay();
    this.updatePuzzleProgressDOM();

    this.buildTrayPanel(tr, pieces);
    const saved = loadPuzzleState(puzzleId, difficulty);
    const applied = this.boardController.applySavedState({
      state: saved as any,
      trayContentSizer: this.trayContentSizer,
      removeFromTraySizer: (trayImg) => (this.trayContentSizer as any)?.remove?.(trayImg, true),
    });
    if (applied) {
      (this.trayContentSizer as any)?.layout?.();
      this.trayPanel?.layout?.();
      this.ensureTrayScrollStateForTutorial();
      this.updateTrayContentMinSize();
      this.updatePuzzleProgressDOM();
    }
    this.isBuildingPuzzle = false;
  }

  private buildTrayPanel(
    tr: { x: number; y: number; w: number; h: number },
    pieces: PieceState[]
  ): void {
    if (this.trayPanelRex) {
      this.trayPanelRex.destroy();
      this.trayPanelRex = null;
    }
    this.trayPanel = null;
    this.trayContentSizer = null;

    const sliderH = 12;
    const gap = 8;
    const { w: tileW, h: tileH } = this.getTrayPieceSize();

    // Гарантируем, что высоты лотка хватит по вертикали под кусочек + слайдер.
    const minTrayH = tileH + sliderH + 8;
    if (tr.h < minTrayH) {
      tr.y = tr.y + tr.h - minTrayH;
      tr.h = minTrayH;
    }
    const cam = this.cameras.main;
    const canvasRect = this.game.canvas.getBoundingClientRect();
    if (canvasRect.height > 0) {
      const domScrollBefore = UiOverlay.getTrayScrollDomRect?.();
      const trayHeightPx = (tr.h * canvasRect.height) / cam.height;
      const canvasBottomPx = canvasRect.top + canvasRect.height;
      const maxHeightInCanvasPx = domScrollBefore ? Math.max(0, canvasBottomPx - domScrollBefore.top) : trayHeightPx;
      const cappedPx = UiOverlay.setTrayScrollHeightPx(Math.min(trayHeightPx, maxHeightInCanvasPx));
      this.trayScrollHeightPx = cappedPx;
      tr.h = (cappedPx / canvasRect.height) * cam.height;
    }
    const domScrollSync = UiOverlay.getTrayScrollDomRect?.();
    if (domScrollSync && canvasRect.height > 0) {
      tr.y = ((domScrollSync.top - canvasRect.top) * cam.height) / canvasRect.height;
    }

    const trayEntries = this.pieceTextureKeys.map((key, i) => ({
      pieceKey: key,
      pieceId: pieces[i]!.id,
    }));
    const activePuzzle = getPuzzleView()?.activePuzzle;
    const storageDifficulty =
      this.mode === 'tutorial'
        ? PuzzleScene.TUTORIAL_PIECE_COUNT
        : (activePuzzle?.difficulty ?? pieces.length);
    const savedTrayOrder =
      activePuzzle ? loadPuzzleTrayOrder(activePuzzle.puzzleId, storageDifficulty) : null;
    if (savedTrayOrder && savedTrayOrder.length > 0) {
      const posById = new Map<number, number>();
      savedTrayOrder.forEach((id, idx) => posById.set(id, idx));
      trayEntries.sort((a, b) => {
        const ai = posById.get(a.pieceId);
        const bi = posById.get(b.pieceId);
        if (ai == null && bi == null) return 0;
        if (ai == null) return 1;
        if (bi == null) return -1;
        return ai - bi;
      });
    } else {
      Phaser.Utils.Array.Shuffle(trayEntries);
    }
    const trayPieceKeys = trayEntries.map((e) => e.pieceKey);
    const trayPieceIds = trayEntries.map((e) => e.pieceId);

    this.trayPanelRex = new TrayPanelRex();
    this.trayPanelRex.build(this, tr, trayPieceKeys, {
      tileW,
      tileH,
      gap,
      sliderH,
      pieceIds: trayPieceIds,
    });
    this.trayPanel = this.trayPanelRex.panel;
    this.trayContentSizer = this.trayPanelRex.contentSizer;
    const trayImages: Phaser.GameObjects.Image[] = this.trayPanelRex.trayImages;
    this.drawTrayBackground();
    this.updateTraySliderVisibility();

    if (this.mode === 'tutorial') {
      this.tutorialController?.destroy();
      this.tutorialController = new TutorialController({
        deps: {
          scene: this,
          trayPanel: this.trayPanel,
          trayContentSizer: this.trayContentSizer,
          getTrayImages: () => this.getTrayImagesForDrag(),
          getTrayRect: () => this.getTrayRectViewportFromDom() ?? this.getTrayRect(),
          getBoardRect: () => this.boardController?.getRectBox() ?? null,
          getBoardTargetRect: (pieceId) => this.boardController?.getCorrectTargetRect(pieceId) ?? null,
        },
        onStepComplete: (step) => {
          if (step === 1) {
            setTutorialStatus('step2');
            this.setTrayWheelScrollEnabled(true);
          }
        },
      });
      if (this.tutorialInitialStep === 2) {
        this.tutorialController.startAtStep2();
        this.setTrayWheelScrollEnabled(true);
      } else {
        this.tutorialController.start();
        this.setTrayWheelScrollEnabled(false);
      }
    }

    if (!this.customDragController) this.customDragController = new CustomDragController(this);
    this.customDragController.attach({
      tr,
      sliderH,
      trayPanel: this.trayPanel,
      trayImages,
      getTrayImages: () => this.getTrayImagesForDrag(),
      callbacks: {
        canStartDrag: (pieceId) => {
          if (this.boardViewportController?.isInteractionBlocked()) return false;
          if (this.boardController?.isPlaced(pieceId)) return false;
          if (this.mode === 'tutorial' && this.tutorialController?.getStep() === 1) {
            return pieceId === this.tutorialController!.getTargetPieceId();
          }
          return true;
        },
        onDropToBoard: ({ pieceId, pieceKey, snap, originalImg }) => {
          const isGuidedStep1 =
            this.mode === 'tutorial' &&
            this.tutorialController?.getStep() === 1 &&
            this.tutorialController?.getTargetPieceId() === pieceId;
          if (isGuidedStep1) {
            const targetRect = this.boardController?.getCorrectTargetRect(pieceId) ?? null;
            const padding = 12;
            const inTarget =
              targetRect &&
              snap.snapX >= targetRect.x - padding &&
              snap.snapX <= targetRect.x + targetRect.w + padding &&
              snap.snapY >= targetRect.y - padding &&
              snap.snapY <= targetRect.y + targetRect.h + padding;
            if (!inTarget) {
              this.returnPieceToTray(pieceId, originalImg);
              this.tutorialController?.onTrayPieceReturnedAfterFailedDrop?.(pieceId, originalImg);
              soundManager.playWrong();
              return;
            }
          }
          this.boardController?.handleDropFromTray({
            pieceId,
            pieceKey,
            snap,
            originalImg,
            removeOriginalFromTray: (img) => (this.trayContentSizer as any)?.remove?.(img, true),
            updateTrayAfterChange: () => this.updateTrayContentMinSize(),
            requestProgressRaf: () => requestAnimationFrame(() => this.updatePuzzleProgressDOM()),
            saveStateAfterDrop: () => this.saveState(),
            onPiecePlaced: (placedPieceId) => {
              // В туториале step1 onPiecePlaced → setTutorialStatus('completed') → profileChanged → refreshPuzzle().
              // Сохраняем state немедленно ДО эмита, иначе пересборка увидит старое сохранение.
              if (isGuidedStep1) this.saveState(true);
              soundManager.playSnap();
              this.events.emit('piece:placed', { pieceId: placedPieceId });
            },
          });
        },
        onReturnToTray: ({ pieceId, originalImg }) => {
          this.returnPieceToTray(pieceId, originalImg);
          soundManager.playWrong();
        },
      },
      snapWorld: (x, y) => this.boardController?.snapWorld(x, y) ?? null,
      getBoardRect: () => this.boardController?.getRectBox() ?? null,
      getBoardPieceSize: () => {
        const piece = this.boardController?.getPieceSize();
        const transform = this.boardController?.getViewportTransform();
        if (!piece) return null;
        const scale = transform?.scale ?? 1;
        return { w: piece.w * scale, h: piece.h * scale };
      },
      onDragStart: () => {
        this.boardViewportController?.setPieceDragging(true);
        this.setDragFocusOverlay(true);
        soundManager.playPick();
      },
      onDragStartWithPiece: (pieceId) => this.tutorialController?.onTrayDragStart(pieceId),
      onDragEnd: () => {
        this.boardViewportController?.setPieceDragging(false);
        this.tutorialController?.onTrayDragEnd();
        this.setDragFocusOverlay(false);
      },
      isScrollEnabled: () =>
        !(this.mode === 'tutorial' && this.tutorialController?.getStep() === 1),
    });
  }

  /** После layout() rexUI может снова включить слайдер — на step1 нужно повторно отключить. */
  private ensureTrayScrollStateForTutorial(): void {
    if (this.mode === 'tutorial' && this.tutorialController?.getStep() === 1) {
      this.setTrayWheelScrollEnabled(false);
    }
  }

  /**
   * Отключить/включить скролл лотка на step1: колёсико мыши + ползунок (slider).
   */
  private setTrayWheelScrollEnabled(enabled: boolean): void {
    const panel = this.trayPanel as any;
    if (!panel) return;

    const scroller = panel.childrenMap?.mouseWheelScroller ?? panel.mouseWheelScroller;
    if (scroller && typeof scroller.setEnable === 'function') {
      scroller.setEnable(enabled);
    } else if (scroller && typeof (scroller as { enable?: boolean }).enable === 'boolean') {
      (scroller as { enable: boolean }).enable = enabled;
    }

    const slider = panel.childrenMap?.slider as Phaser.GameObjects.GameObject & { setEnable?: (v: boolean) => void };
    if (slider) {
      if (typeof slider.setEnable === 'function') {
        slider.setEnable(enabled);
      } else {
        this.setGameObjectInteractive(slider, enabled);
      }
    }
  }

  /** Рекурсивно включить/выключить input у объекта и дочерних (для ползунка rexUI). */
  private setGameObjectInteractive(obj: Phaser.GameObjects.GameObject, enabled: boolean): void {
    if (enabled) {
      if ('setInteractive' in obj && typeof (obj as Phaser.GameObjects.GameObject).setInteractive === 'function') {
        (obj as Phaser.GameObjects.GameObject).setInteractive({ useHandCursor: false });
      }
    } else {
      if ('disableInteractive' in obj && typeof (obj as Phaser.GameObjects.GameObject).disableInteractive === 'function') {
        (obj as Phaser.GameObjects.GameObject).disableInteractive();
      }
    }
    const list = (obj as Phaser.GameObjects.Container).list;
    if (Array.isArray(list)) {
      for (const child of list) {
        this.setGameObjectInteractive(child as Phaser.GameObjects.GameObject, enabled);
      }
    }
  }

  /** Создаёт оверлей «рамки» вокруг поля (4 rect) для мягкого затемнения фона при drag. */
  private createFocusOverlay(): void {
    const r = this.boardController?.getRectBox() ?? null;
    if (!r) return;
    const cam = this.cameras.main;
    const camW = cam.width;
    const camH = cam.height;
    if (this.focusOverlayContainer) {
      this.focusOverlayContainer.destroy();
      this.focusOverlayContainer = null;
    }
    const container = this.add.container(0, 0).setScrollFactor(0).setDepth(4000).setAlpha(0);
    const alpha = 0.1;
    const top = this.add.graphics().setScrollFactor(0).fillStyle(0x000000, alpha).fillRect(0, 0, camW, r.y);
    const bottom = this.add
      .graphics()
      .setScrollFactor(0)
      .fillStyle(0x000000, alpha)
      .fillRect(0, r.y + r.h, camW, Math.max(0, camH - (r.y + r.h)));
    const left = this.add.graphics().setScrollFactor(0).fillStyle(0x000000, alpha).fillRect(0, r.y, r.x, r.h);
    const right = this.add
      .graphics()
      .setScrollFactor(0)
      .fillStyle(0x000000, alpha)
      .fillRect(r.x + r.w, r.y, Math.max(0, camW - (r.x + r.w)), r.h);
    container.add([top, bottom, left, right]);
    this.focusOverlayContainer = container;
    this.input.on('dragstart', this.dragFocusOnHandler);
    this.input.on('dragend', this.dragFocusOffHandler);
  }

  private dragFocusOnHandler = (_pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.GameObject): void => {
    if (gameObject.getData?.('where') === 'board') this.setDragFocusOverlay(true);
  };

  private dragFocusOffHandler = (_pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.GameObject): void => {
    if (gameObject.getData?.('where') === 'board') this.setDragFocusOverlay(false);
  };

  /** Плавное затемнение фона вне поля (4–6%) при drag, возврат при отпускании. */
  private setDragFocusOverlay(active: boolean): void {
    if (!this.focusOverlayContainer) return;
    this.tweens.killTweensOf(this.focusOverlayContainer);
    this.tweens.add({
      targets: this.focusOverlayContainer,
      alpha: active ? 1 : 0,
      duration: 180,
      ease: 'Linear',
    });
  }

  private destroyBoardAndTray(): void {
    this.input.off('dragstart', this.dragFocusOnHandler);
    this.input.off('dragend', this.dragFocusOffHandler);
    if (this.focusOverlayContainer) {
      this.tweens.killTweensOf(this.focusOverlayContainer);
      this.focusOverlayContainer.destroy();
      this.focusOverlayContainer = null;
    }
    if (this.tutorialController) {
      this.tutorialController.destroy();
      this.tutorialController = null;
      if (this.mode === 'tutorial') UiOverlay.setTopBarMode('normal');
      this.setTrayWheelScrollEnabled(true);
    }
    this.customDragController?.detach();
    this.boardViewportController?.setPieceDragging(false);
    this.boardViewportController?.destroy();
    this.boardViewportController = null;
    this.boardController?.setInteractionBlockedPredicate(null);
    this.boardController?.destroy();
    this.puzzleProgress?.reset();
    this.trayContentSizer = null;
    if (this.trayBackgroundGraphics) {
      this.trayBackgroundGraphics.destroy();
      this.trayBackgroundGraphics = null;
    }
    if (this.trayPanelRex) {
      this.trayPanelRex.destroy();
      this.trayPanelRex = null;
    }
    this.trayPanel = null;
    this.trayScrollHeightPx = 0;
    this.trayPieceDisplayW = 0;
    this.trayPieceDisplayH = 0;
    PieceTextureFactory.destroy(this, this.pieceTextureKeys);
    this.pieceTextureKeys = [];
  }

  /**
   * Размер куска в лотке:
   * - tutorial: как у текущей доски, но с учётом desktop-тюнинга trayPieceDisplay;
   * - normal: фиксируем "эталон" как будто пазл на сложности 24.
   */
  private getTrayPieceSize(fallback: { w: number; h: number } = { w: 0, h: 0 }): { w: number; h: number } {
    if (this.trayPieceDisplayW > 0 && this.trayPieceDisplayH > 0) {
      return { w: this.trayPieceDisplayW, h: this.trayPieceDisplayH };
    }
    return this.boardController?.getPieceSize() ?? fallback;
  }

  /**
   * Вычисляет фиксированный размер куска для лотка.
   * Эталон — геометрия доски при сложности 24, чтобы визуально совпадало с прежним поведением daily.
   */
  private computeTrayPieceSize(args: {
    difficulty: number;
    imageWidth: number;
    imageHeight: number;
    safeRect: { left: number; top: number; width: number; height: number };
    sourcePieceW: number;
    sourcePieceH: number;
  }): { w: number; h: number } {
    const referenceDifficulty =
      this.mode === 'tutorial' ? PuzzleScene.TUTORIAL_PIECE_COUNT : this.TRAY_REFERENCE_DIFFICULTY;
    const { rows, cols } = getRowsCols(referenceDifficulty, args.imageWidth, args.imageHeight);
    const scale = Math.min(
      (args.safeRect.width * BOARD_SAFE_FIT_RATIO) / args.imageWidth,
      (args.safeRect.height * BOARD_SAFE_FIT_RATIO) / args.imageHeight
    );
    const boardW = args.imageWidth * scale * BOARD_CONTENT_FILL_RATIO;
    const boardH = args.imageHeight * scale * BOARD_CONTENT_FILL_RATIO;
    const cellW = boardW / cols;
    const cellH = boardH / rows;
    const tab = Math.max(0, Math.min(cellW, cellH) * Math.max(0, tabRatioFor(referenceDifficulty)));
    let trayW = cellW + tab * 2;
    let trayH = cellH + tab * 2;

    const adaptiveDifficulties = new Set<number>(TRAY_TUNED_DIFFICULTIES as readonly number[]);
    if (this.mode !== 'tutorial' && adaptiveDifficulties.has(args.difficulty)) {
      const referenceDifficulty = TRAY_SIZE_REFERENCE_BY_DIFFICULTY[args.difficulty] ?? args.difficulty;
      let sourceWForCap = args.sourcePieceW;
      let sourceHForCap = args.sourcePieceH;
      if (referenceDifficulty !== args.difficulty) {
        const { rows: refRows, cols: refCols } = getRowsCols(
          referenceDifficulty,
          args.imageWidth,
          args.imageHeight
        );
        const refCellW = args.imageWidth / refCols;
        const refCellH = args.imageHeight / refRows;
        const refTabRatio = tabRatioFor(referenceDifficulty);
        const refTabPx = Math.max(
          0,
          Math.round(Math.min(refCellW, refCellH) * Math.max(0, refTabRatio))
        );
        sourceWForCap = refCellW + refTabPx * 2;
        sourceHForCap = refCellH + refTabPx * 2;
      }
      const dpr = typeof window !== 'undefined' ? Math.max(1, window.devicePixelRatio || 1) : 1;
      const maxWBySource = (sourceWForCap * TRAY_MAX_SOURCE_UPSCALE_PHYSICAL) / dpr;
      const maxHBySource = (sourceHForCap * TRAY_MAX_SOURCE_UPSCALE_PHYSICAL) / dpr;
      const capW = Math.max(Math.min(maxWBySource, trayW), Math.min(TRAY_MIN_PIECE_CSS_PX, trayW));
      const capH = Math.max(Math.min(maxHBySource, trayH), Math.min(TRAY_MIN_PIECE_CSS_PX, trayH));
      trayW = Math.min(trayW, capW);
      trayH = Math.min(trayH, capH);
    }

    return { w: trayW, h: trayH };
  }

  private destroyPuzzle(): void {
    this.scale.off('resize', this.resizeHandler);
    this.destroyBoardAndTray();
  }

  /** Картинка для обучения: всегда одна и та же. */
  private static readonly TUTORIAL_IMAGE_PATH = 'assets/img/study.png';
  private static readonly TUTORIAL_TEXTURE_KEY = 'puzzle-full-tutorial';
  /** В режиме обучения всегда 16 кусочков. */
  private static readonly TUTORIAL_PIECE_COUNT = 16;

  private loadFullTextureIfNeeded(): void {
    const view = getPuzzleView();
    if (!view.activePuzzle) {
      this.setPuzzleLoadingOverlayVisible(false);
      this.destroyPuzzle();
      return;
    }

    let url: string;
    let textureKey: string;

    if (this.mode === 'tutorial') {
      textureKey = PuzzleScene.TUTORIAL_TEXTURE_KEY;
      url = resolvePublicAssetPath(PuzzleScene.TUTORIAL_IMAGE_PATH);
    } else {
      const meta = getPuzzleMeta(view.activePuzzle.puzzleId);
      if (!meta?.imagePath) return;
      url = resolvePublicAssetPath(meta.imagePath);
      textureKey = `puzzle-full-${meta.imageKey ?? meta.id}`;
    }

    if (this.textures.exists(textureKey)) {
      // Не пересобирать пазл в туториале при Step 2: profileChanged → refreshPuzzle → сюда.
      // showPuzzleHud пересоздаёт DOM лотка — восстанавливаем высоту, чтобы лоток не сжимался.
      if (this.mode === 'tutorial' && this.tutorialController?.getStep() === 2) {
        if (this.trayScrollHeightPx > 0) {
          UiOverlay.setTrayScrollHeightPx(this.trayScrollHeightPx);
          this.tutorialController.layout();
        }
        this.setPuzzleLoadingOverlayVisible(false);
        return;
      }
      // Даём DOM один кадр на отрисовку loading-overlay,
      // иначе при кешированной текстуре сборка может начаться сразу и оверлей не будет виден.
      requestAnimationFrame(() => this.buildPuzzle(textureKey));
      return;
    }

    this.load.image(textureKey, url);

    this.load.once('complete', () => {
      requestAnimationFrame(() =>
        this.buildPuzzle(textureKey)
      );
    });
    this.load.once('loaderror', () => {
      // текстура не появится, превью не показываем
      this.setPuzzleLoadingOverlayVisible(false);
    });
    this.load.start();
  }

  private buildPuzzle(textureKey: string): void {
    this.destroyPuzzle();
    try {
      const safeRect = PuzzleLayout.computeSafeRect(this);
      if (!safeRect) return;

      const tex = this.textures.get(textureKey).getSourceImage() as HTMLImageElement;
      this.buildPuzzlePieces(textureKey, tex.width, tex.height, safeRect);
      this.scale.on('resize', this.resizeHandler);
    } finally {
      this.setPuzzleLoadingOverlayVisible(false);
    }
  }

  private refreshPuzzle = (): void => {
    if (this.isCompletingPuzzle) return;
    const view = getPuzzleView();

    if (!view.activePuzzle) {
      this.setPuzzleLoadingOverlayVisible(false);
      this.destroyPuzzle();
      this.goToReturnTarget();
      return;
    }

    const profile = loadProfile();
    const pieceCount =
      this.mode === 'tutorial' ? PuzzleScene.TUTORIAL_PIECE_COUNT : view.activePuzzle.difficulty;
    const canUseViewportZoom =
      this.mode !== 'tutorial' && pieceCount >= BOARD_VIEWPORT_ZOOM_FROM_DIFFICULTY;
    UiOverlay.showPuzzleHud(
      { coins: profile.coins, pieceCount },
      {
        onBack: () => {
          if (this.mode === 'tutorial') {
            setTutorialStatus('skipped');
            clearActivePuzzle();
          }
          this.saveState(true);
          this.goToReturnTarget();
        },
        onZoomOut: canUseViewportZoom ? () => this.boardViewportController?.zoomOut() : undefined,
        onZoomIn: canUseViewportZoom ? () => this.boardViewportController?.zoomIn() : undefined,
        getZoomPercent: canUseViewportZoom
          ? () => this.boardViewportController?.getZoomPercent() ?? 100
          : undefined,
      }
    );
    if (this.mode === 'tutorial') {
      UiOverlay.setTopBarMode('tutorial', {
        onSkip: () => {
          setTutorialStatus('skipped');
          clearActivePuzzle();
          this.saveState(true);
          this.goToReturnTarget();
        },
      });
    }
    this.setPuzzleLoadingOverlayVisible(this.shouldShowPuzzleLoadingOverlay(pieceCount));
    requestAnimationFrame(() => {
      this.recalcAndLayoutPreview();
    });
  };

  /** Пересчёт и раскладка превью после рендера DOM (вызывать после showPuzzleHud в rAF). */
  private recalcAndLayoutPreview(): void {
    this.loadFullTextureIfNeeded();
  }

  update(): void {
    if (this.mode === 'tutorial' && this.tutorialController) {
      this.tutorialLayoutTick++;
      if (this.tutorialLayoutTick % 6 === 0) this.tutorialController.layout();
    }
  }

  create(): void {
    soundManager.bind(this);
    this.input.setTopOnly(true);

    const cam = this.cameras.main;
    cam.setViewport(0, 0, this.scale.width, this.scale.height);
    cam.setScroll(0, 0);
    cam.setZoom(1);

    if (!this.puzzleProgress) {
      this.puzzleProgress = new PuzzleProgress({
        onCompleted: () => this.showPuzzleCompleteFlow(),
      });
    }
    if (!this.boardController) {
      this.boardController = new BoardController(this, {
        snapRadius: SNAP_RADIUS,
        callbacks: {
          playFloatingCheckmark: (x, y) => this.playFloatingCheckmark(x, y),
          playSeamGlow: (args) => this.playSeamGlow(args),
          updatePuzzleProgressDOM: () => this.updatePuzzleProgressDOM(),
          saveState: () => this.saveState(),
          returnBoardPieceToTray: (pieceId, pieceKey, tileW, tileH) =>
            this.returnBoardPieceToTray(pieceId, pieceKey, tileW, tileH),
        },
      });
    }
    this.boardController.attachInputHandlers();

    // Сохраняем при уходе со страницы (закрытие вкладки, обновление, переход) — в shutdown сцена уже пустая, там не сохраняем
    const sceneRef = this;
    const saveOnLeave = (): void => {
      if (sceneRef.scene.isActive()) sceneRef.saveState(true);
    };
    const onVisibilityHidden = (): void => {
      if (document.hidden && sceneRef.scene.isActive()) sceneRef.saveState();
    };
    window.addEventListener('beforeunload', saveOnLeave);
    window.addEventListener('pagehide', saveOnLeave);
    document.addEventListener('visibilitychange', onVisibilityHidden);

    this.events.once('shutdown', () => {
      off('profileChanged', this.refreshPuzzle);
      if (this.saveStateTimeout != null) {
        clearTimeout(this.saveStateTimeout);
        this.saveStateTimeout = null;
      }
      window.removeEventListener('beforeunload', saveOnLeave);
      window.removeEventListener('pagehide', saveOnLeave);
      document.removeEventListener('visibilitychange', onVisibilityHidden);
      this.setPuzzleLoadingOverlayVisible(false);
      this.destroyPuzzle();
      UiOverlay.hide();
    });

    on('profileChanged', this.refreshPuzzle);
    this.refreshPuzzle();
  }
}
